import type { Lang } from "@/i18n/locale";

interface NumberVocabulary {
  readonly integers: ReadonlyMap<string, number>;
  readonly digits: ReadonlyMap<string, number>;
  readonly words: ReadonlySet<string>;
  readonly conjunctions: ReadonlySet<string>;
  readonly decimals: ReadonlySet<string>;
}
const normal = (value: string) => value.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
const canonical = (value: string) => normal(value).replace(/[-\u2010\u2011]/gu, " ").replace(/\s+/gu, " ").trim();
const alternatives = (value: string) => value.split("|");

// Original, bounded recognizer vocabulary. These are number spellings, not UI copy.
function builder(small: readonly string[], conjunctions: string, decimals: string, unsupported: string) {
  const integers = new Map<string, number>(), digits = new Map<string, number>();
  const words = new Set<string>(), joins = new Set(alternatives(conjunctions).filter(Boolean).map(normal));
  const points = new Set(alternatives(decimals).map(normal));
  const add = (spelling: string, value: number) => {
    const key = canonical(spelling);
    integers.set(key, value);
    for (const word of key.split(" ")) words.add(word);
    if (value < 10) digits.set(key, value);
  };
  small.forEach((spellings, value) => alternatives(spellings).forEach(spelling => add(spelling, value)));
  for (const word of alternatives(unsupported)) if (word) words.add(normal(word));
  const tens = (spellings: readonly string[], join: string) => {
    spellings.forEach((variants, index) => {
      const value = 20 + index * 10;
      for (const ten of alternatives(variants).filter(Boolean)) {
        add(ten, value);
        for (let unit = 1; unit < 10; unit++) for (const one of alternatives(small[unit]))
          add([ten, join, one].filter(Boolean).join(" "), value + unit);
      }
    });
  };
  const finish = (): NumberVocabulary => ({ integers, digits, words, conjunctions: joins, decimals: points });
  return { add, tens, finish, small };
}

function english(): NumberVocabulary {
  const b = builder(
    ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"],
    "and", "point|comma", "thousand|million|billion|trillion",
  );
  b.tens(["twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"], "");
  b.add("hundred", 100); b.add("one hundred", 100);
  return b.finish();
}
function portuguese(): NumberVocabulary {
  const b = builder(
    ["zero", "um|uma", "dois|duas", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "catorze|quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"],
    "e", "vírgula|ponto", "cento|duzentos|duzentas|mil|milhão|milhões|bilhão|bilhões",
  );
  b.tens(["vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"], "e");
  b.add("cem", 100);
  return b.finish();
}
function dutch(): NumberVocabulary {
  const b = builder(
    ["nul", "een", "twee", "drie", "vier", "vijf", "zes", "zeven", "acht", "negen", "tien", "elf", "twaalf", "dertien", "veertien", "vijftien", "zestien", "zeventien", "achttien", "negentien"],
    "en", "komma|punt", "duizend|miljoen|miljard",
  );
  ["twintig", "dertig", "veertig", "vijftig", "zestig", "zeventig", "tachtig", "negentig"].forEach((ten, index) => {
    const value = 20 + index * 10;
    b.add(ten, value);
    for (let unit = 1; unit < 10; unit++) {
      b.add(b.small[unit] + "en" + ten, value + unit);
      b.add(b.small[unit] + " en " + ten, value + unit);
    }
  });
  b.add("honderd", 100); b.add("eenhonderd", 100); b.add("een honderd", 100);
  return b.finish();
}
function german(): NumberVocabulary {
  const b = builder(
    ["null", "eins|ein|eine", "zwei", "drei", "vier", "fünf", "sechs", "sieben", "acht", "neun", "zehn", "elf", "zwölf", "dreizehn", "vierzehn", "fünfzehn", "sechzehn", "siebzehn", "achtzehn", "neunzehn"],
    "und", "komma|punkt", "tausend|million|millionen|milliarde|milliarden",
  );
  ["zwanzig", "dreißig|dreissig", "vierzig", "fünfzig", "sechzig", "siebzig", "achtzig", "neunzig"].forEach((variants, index) => {
    const value = 20 + index * 10;
    for (const ten of alternatives(variants)) {
      b.add(ten, value);
      for (let unit = 1; unit < 10; unit++) {
        const one = unit === 1 ? "ein" : b.small[unit];
        b.add(one + "und" + ten, value + unit);
        b.add(one + " und " + ten, value + unit);
      }
    }
  });
  b.add("hundert", 100); b.add("einhundert", 100); b.add("ein hundert", 100);
  return b.finish();
}
function italian(): NumberVocabulary {
  const b = builder(
    ["zero", "uno|una|un", "due", "tre", "quattro", "cinque", "sei", "sette", "otto", "nove", "dieci", "undici", "dodici", "tredici", "quattordici", "quindici", "sedici", "diciassette", "diciotto", "diciannove"],
    "e", "virgola|punto", "duecento|mille|mila|milione|milioni|miliardo|miliardi",
  );
  ["venti", "trenta", "quaranta", "cinquanta", "sessanta", "settanta", "ottanta", "novanta"].forEach((ten, index) => {
    const value = 20 + index * 10;
    b.add(ten, value);
    for (let unit = 1; unit < 10; unit++) for (const one of alternatives(b.small[unit]))
      b.add((unit === 1 || unit === 8 ? ten.slice(0, -1) : ten) + one, value + unit);
  });
  b.add("cento", 100);
  return b.finish();
}
function french(): NumberVocabulary {
  const b = builder(
    ["zéro", "un|une", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix sept", "dix huit", "dix neuf"],
    "et", "virgule|point", "cents|mille|million|millions|milliard|milliards",
  );
  ["vingt", "trente", "quarante", "cinquante", "soixante"].forEach((ten, index) => {
    const value = 20 + index * 10;
    b.add(ten, value);
    for (let unit = 1; unit < 10; unit++) for (const one of alternatives(b.small[unit]))
      b.add([ten, unit === 1 ? "et" : "", one].filter(Boolean).join(" "), value + unit);
  });
  for (let value = 70; value < 80; value++)
    b.add("soixante " + (value === 71 ? "et " : "") + b.small[value - 60], value);
  b.add("quatre vingt", 80); b.add("quatre vingts", 80);
  for (let value = 81; value < 100; value++) for (const suffix of alternatives(b.small[value - 80]))
    b.add("quatre vingt " + suffix, value);
  b.add("cent", 100);
  return b.finish();
}
function spanish(): NumberVocabulary {
  const b = builder(
    ["cero", "uno|un|una", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve"],
    "y", "coma|punto", "ciento|cientos|doscientos|doscientas|mil|millón|millones",
  );
  ["veinte", "veintiuno|veintiún|veintiuna", "veintidós", "veintitrés", "veinticuatro", "veinticinco", "veintiséis", "veintisiete", "veintiocho", "veintinueve"].forEach((variants, index) => {
    for (const spelling of alternatives(variants)) b.add(spelling, 20 + index);
  });
  b.tens(["", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"], "y");
  b.add("cien", 100);
  return b.finish();
}
function polish(): NumberVocabulary {
  const b = builder(
    ["zero", "jeden|jedna|jedno", "dwa|dwie", "trzy", "cztery", "pięć", "sześć", "siedem", "osiem", "dziewięć", "dziesięć", "jedenaście", "dwanaście", "trzynaście", "czternaście", "piętnaście", "szesnaście", "siedemnaście", "osiemnaście", "dziewiętnaście"],
    "i", "przecinek|kropka", "dwieście|tysiąc|tysiące|tysięcy|milion|miliony|milionów",
  );
  b.tens(["dwadzieścia", "trzydzieści", "czterdzieści", "pięćdziesiąt", "sześćdziesiąt", "siedemdziesiąt", "osiemdziesiąt", "dziewięćdziesiąt"], "");
  b.add("sto", 100);
  return b.finish();
}
function bulgarian(): NumberVocabulary {
  const b = builder(
    ["нула", "едно|един|една", "две|два", "три", "четири", "пет", "шест", "седем", "осем", "девет", "десет", "единадесет|единайсет", "дванадесет|дванайсет", "тринадесет|тринайсет", "четиринадесет|четиринайсет", "петнадесет|петнайсет", "шестнадесет|шестнайсет", "седемнадесет|седемнайсет", "осемнадесет|осемнайсет", "деветнадесет|деветнайсет"],
    "и", "запетая|точка", "двеста|хиляда|хиляди|милион|милиона|милиони|милиард|милиарда",
  );
  b.tens(["двадесет|двайсет", "тридесет|трийсет", "четиридесет|четирийсет", "петдесет", "шестдесет", "седемдесет", "осемдесет", "деветдесет"], "и");
  b.add("сто", 100);
  return b.finish();
}

const vocabulary: Record<Lang, NumberVocabulary> = {
  EN: english(), "PT-BR": portuguese(), NL: dutch(), DE: german(), IT: italian(),
  FR: french(), ES: spanish(), PL: polish(), BG: bulgarian(),
};

function integer(phrase: string, words: NumberVocabulary): number | undefined {
  if (/^\d{1,3}$/u.test(phrase)) {
    const value = Number(phrase);
    return value <= 100 ? value : undefined;
  }
  return words.integers.get(phrase);
}
function fraction(phrase: string, words: NumberVocabulary): string | undefined {
  if (/^\d{1,2}$/u.test(phrase)) return phrase;
  const cardinal = words.integers.get(phrase);
  if (cardinal !== undefined && cardinal < 100) return String(cardinal);
  const tokens = phrase.split(" ");
  if (tokens.length !== 2) return undefined;
  const digits = tokens.map(token => /^\d$/u.test(token) ? Number(token) : words.digits.get(token));
  return digits.every(value => value !== undefined) ? digits.join("") : undefined;
}
interface Token { readonly text: string; readonly key: string; readonly start: number; readonly end: number }
function converted(tokens: readonly Token[], text: string, words: NumberVocabulary): string | undefined {
  // A conjunction at a unit boundary belongs to the surrounding clause. Keep it.
  let first = 0, last = tokens.length;
  while (first < last && words.conjunctions.has(tokens[first].key)) first++;
  while (last > first && words.conjunctions.has(tokens[last - 1].key)) last--;
  if (first === last) return undefined;
  const number = tokens.slice(first, last);
  if (number.every(token => /^\d/u.test(token.key))) return undefined;
  const phrase = canonical(text.slice(number[0].start, number[number.length - 1].end));
  const decimal = number.filter(token => words.decimals.has(token.key));
  let value: string | undefined;
  if (!decimal.length) {
    const whole = integer(phrase, words);
    if (whole !== undefined) value = String(whole);
  } else if (decimal.length === 1) {
    const point = decimal[0];
    const whole = integer(canonical(text.slice(number[0].start, point.start)), words);
    const part = fraction(canonical(text.slice(point.end, number[number.length - 1].end)), words);
    if (whole !== undefined && part !== undefined) value = String(whole) + "." + part;
  }
  if (value === undefined) return undefined;
  return text.slice(tokens[0].start, number[0].start) + value
    + text.slice(number[number.length - 1].end, tokens[tokens.length - 1].end);
}

/**
 * Convert exact spoken cardinal phrases (0..100) and explicit decimals (1..2
 * fractional digits). Preserve every surrounding/unknown word and punctuation.
 * Maximal numeral runs must match in full: "one hundred and one" is unchanged.
 * This is preprocessing only; callers must still use the strict unit/set parser.
 * Digit-only input is unchanged; no fractions, arithmetic or inferred units.
 */
export function normalizeWorkoutSpokenNumbers(text: string, lang: Lang): string {
  if (text.length > 240) return text;
  const words = Object.prototype.hasOwnProperty.call(vocabulary, lang) ? vocabulary[lang] : undefined;
  if (!words) return text;
  const tokens: Token[] = [...text.matchAll(/[\p{L}\p{M}]+|\d+(?:[.,]\d+)?/gu)].map(match => ({
    text: match[0], key: normal(match[0]), start: match.index!, end: match.index! + match[0].length,
  }));
  const eligible = (token: Token) => /^\d/u.test(token.key) || words.words.has(token.key)
    || words.conjunctions.has(token.key) || words.decimals.has(token.key);
  let output = "", cursor = 0;
  for (let index = 0; index < tokens.length; index++) {
    if (!eligible(tokens[index])) continue;
    let end = index + 1;
    // Keep touching words/digits and decimal punctuation in the same run, so
    // separate replacements cannot accidentally create a different numeric literal.
    // Unspaced hyphens are numeral spelling; spaced signs/ranges stay separate.
    while (end < tokens.length && eligible(tokens[end])
      && /^(?:\s+|[-\u2010\u2011.,])?$/u.test(text.slice(tokens[end - 1].end, tokens[end].start))) end++;
    const group = tokens.slice(index, end), value = converted(group, text, words);
    if (value !== undefined) {
      output += text.slice(cursor, group[0].start) + value;
      cursor = group[group.length - 1].end;
    }
    index = end - 1;
  }
  return output + text.slice(cursor);
}
